import { supabase } from '../lib/supabase';

// Helper function to filter out test/fake data
const isTestData = (email, companyName, title) => {
  const testPatterns = [
    /test/i,
    /dummy/i,
    /fake/i,
    /sample/i,
    /example/i,
    /@test\./i,
    /@example\./i,
    /demo/i
  ];
  
  const textToCheck = [email, companyName, title]?.filter(Boolean)?.join(' ');
  return testPatterns?.some(pattern => pattern?.test(textToCheck));
};

export const analyticsService = {
  // Get overview metrics for dashboard
  async getOverviewMetrics() {
    const { data, error } = await supabase?.from('admin_analytics_summary')?.select('*')?.maybeSingle();
    
    if (error) throw error;
    
    // Get real user count (excluding test users)
    const { data: realUsers } = await supabase
      ?.from('user_profiles')
      ?.select('email')
      ?.not('email', 'ilike', '%test%')
      ?.not('email', 'ilike', '%dummy%')
      ?.not('email', 'ilike', '%fake%')
      ?.not('email', 'ilike', '%example%')
      ?.not('email', 'ilike', '%demo%');
    
    // Get real vouchers count (excluding test vouchers)
    const { data: realVouchers } = await supabase
      ?.from('vouchers')
      ?.select('id, title, company_name')
      ?.eq('voucher_status', 'active')
      ?.not('title', 'ilike', '%test%')
      ?.not('title', 'ilike', '%dummy%')
      ?.not('company_name', 'ilike', '%test%')
      ?.not('company_name', 'ilike', '%dummy%');
    
    return {
      totalUsers: realUsers?.length || 0,
      newUsersLastMonth: data?.new_users_last_month || 0,
      activeVouchers: realVouchers?.length || 0,
      totalTransactions: data?.total_transactions || 0,
      totalRevenue: data?.total_revenue || 0,
      transactionsLastMonth: data?.transactions_last_month || 0,
      averageRating: parseFloat(data?.average_rating || 0)?.toFixed(2),
      reviewsLastMonth: data?.reviews_last_month || 0
    };
  },

  // Get user growth data
  async getUserGrowthData(days = 30) {
    const { data, error } = await supabase
      ?.from('user_profiles')
      ?.select('created_at, email')
      ?.gte('created_at', new Date(Date.now() - days * 24 * 60 * 60 * 1000)?.toISOString())
      ?.not('email', 'ilike', '%test%')
      ?.not('email', 'ilike', '%dummy%')
      ?.not('email', 'ilike', '%fake%')
      ?.not('email', 'ilike', '%example%')
      ?.not('email', 'ilike', '%demo%')
      ?.order('created_at', { ascending: true });
    
    if (error) throw error;
    
    // Group by date
    const growthByDate = {};
    data?.forEach(user => {
      const date = new Date(user.created_at)?.toLocaleDateString();
      growthByDate[date] = (growthByDate?.[date] || 0) + 1;
    });
    
    return Object.entries(growthByDate)?.map(([date, count]) => ({
      date,
      users: count
    }));
  },

  // Get transaction trends
  async getTransactionTrends(days = 30) {
    const { data, error } = await supabase
      ?.from('transactions')
      ?.select('transaction_date, amount, transaction_status')
      ?.gte('transaction_date', new Date(Date.now() - days * 24 * 60 * 60 * 1000)?.toISOString())
      ?.order('transaction_date', { ascending: true });
    
    if (error) throw error;
    
    // Group by date and status
    const trendsByDate = {};
    data?.forEach(transaction => {
      const date = new Date(transaction.transaction_date)?.toLocaleDateString();
      if (!trendsByDate?.[date]) {
        trendsByDate[date] = { date, completed: 0, pending: 0, failed: 0, revenue: 0 };
      }
      trendsByDate[date][transaction.transaction_status] += 1;
      if (transaction?.transaction_status === 'completed') {
        trendsByDate[date].revenue += parseFloat(transaction?.amount);
      }
    });
    
    return Object.values(trendsByDate);
  },

  // Get voucher statistics by category
  async getVoucherStatsByCategory() {
    const { data, error } = await supabase
      ?.from('vouchers')
      ?.select(`
        id,
        voucher_status,
        title,
        company_name,
        categories (
          id,
          name
        )
      `)
      ?.not('title', 'ilike', '%test%')
      ?.not('title', 'ilike', '%dummy%')
      ?.not('title', 'ilike', '%fake%')
      ?.not('company_name', 'ilike', '%test%')
      ?.not('company_name', 'ilike', '%dummy%');
    
    if (error) throw error;
    
    // Group by category
    const statsByCategory = {};
    data?.forEach(voucher => {
      const categoryName = voucher?.categories?.name || 'Uncategorized';
      if (!statsByCategory?.[categoryName]) {
        statsByCategory[categoryName] = { active: 0, pending: 0, sold: 0, expired: 0 };
      }
      statsByCategory[categoryName][voucher.voucher_status] += 1;
    });
    
    return Object.entries(statsByCategory)?.map(([name, stats]) => ({
      category: name,
      ...stats,
      total: stats?.active + stats?.pending + stats?.sold + stats?.expired
    }));
  },

  // Get top performing vouchers
  async getTopVouchers(limit = 10) {
    const { data, error } = await supabase
      ?.from('vouchers')
      ?.select('id, title, company_name, views_count, voucher_status')
      ?.not('title', 'ilike', '%test%')
      ?.not('title', 'ilike', '%dummy%')
      ?.not('title', 'ilike', '%fake%')
      ?.not('company_name', 'ilike', '%test%')
      ?.not('company_name', 'ilike', '%dummy%')
      ?.order('views_count', { ascending: false })
      ?.limit(limit);
    
    if (error) throw error;
    
    return data?.map(voucher => ({
      id: voucher?.id,
      title: voucher?.title,
      companyName: voucher?.company_name,
      viewsCount: voucher?.views_count,
      voucherStatus: voucher?.voucher_status
    }));
  },

  // Get revenue breakdown
  async getRevenueBreakdown(days = 30) {
    const { data, error } = await supabase
      ?.from('transactions')
      ?.select('amount, payment_method, transaction_status')
      ?.eq('transaction_status', 'completed')
      ?.gte('transaction_date', new Date(Date.now() - days * 24 * 60 * 60 * 1000)?.toISOString());
    
    if (error) throw error;
    
    const breakdown = {
      total: 0,
      byPaymentMethod: {}
    };
    
    data?.forEach(transaction => {
      const amount = parseFloat(transaction?.amount);
      breakdown.total += amount;
      
      const method = transaction?.payment_method || 'Unknown';
      breakdown.byPaymentMethod[method] = (breakdown?.byPaymentMethod?.[method] || 0) + amount;
    });
    
    return breakdown;
  }
};